export type Country = {
  name: string;
  code: string;
};

// Права Юзера
export enum EUserRole {
  User = 'user',
  Admin = 'admin',
  Manager = 'manager',
}

export enum EUserSex {
  Female = 'female',
  Male = 'male',
}

export enum ETypeVaccine {
  Vaccination = 'vaccination', // вакцинация
  ReVaccination = 're_vaccination', // ревакцинация
}

export enum EDiseaseStatus {
  Basic = 'basic', // базовая (вроде как обязательная, но можно не ставить)
  Mandatory = 'mandatory', // обязательная
  Additional = 'additional', // дополнительаня
  Epidemic = 'epidemic', // эпидимиологическая
}

export const DiseaseStatusText = {
  [EDiseaseStatus.Basic]: 'Basic vaccination',
  [EDiseaseStatus.Mandatory]: 'Mandatory vaccination',
  [EDiseaseStatus.Additional]: 'Additional vaccination',
  [EDiseaseStatus.Epidemic]: 'Epidemic vaccination',
};

export type People = {
  _id: string;
  name: string;
  sex: EUserSex;
  birthday: string;
  country: Country;
  is_owner: boolean;
  listDisease: Array<{
    _id: string;
    code: string;
    name: string;
  }>;
  listDoneVaccinations: Array<{
    _id: string;
    diseaseId: string;
    vaccineId: string;
    date: string;
    clinic: string;
    comments: string;
    reactions: string;
    type_vaccination: ETypeVaccine;
    round: number;
  }>;
};

// Питомец юзера (поля, нужные админке)
export type UserPet = {
  _id: string;
  name: string;
  specie: ESpecie;
  birthday: string;
  country: Country;
  listDisease: Array<{ _id: string; code: string; name: string }>;
  listDoneVaccinations: Array<{
    _id: string;
    diseaseId: string;
    vaccineId: string;
    date: string;
  }>;
};

// Субъект пояснения по болезни
export type ExplainSubjectKind = 'people' | 'pet';

// Ответ пояснения (facts/reconciliation намеренно не типизируются)
export type ExplainResponse = { markdown: string };

// Модель Юзера
export type User = {
  email: string;
  role: EUserRole;
  token: string;
  expo_tokens: string[];
  peoples: People[];
  pets?: UserPet[];
  descriptions: string;
};

export type UserPlatform = 'ios' | 'android';

// Стадия регистрации аккаунта (воронка). Порядок значим: sortBy=stage сортирует по воронке, не по алфавиту.
export enum EUserOnboardingStage {
  CodeNotInserted = 'CodeNotInserted',
  NoOwnerProfile = 'NoOwnerProfile',
  NoVaccinations = 'NoVaccinations',
  OnboardingCompleted = 'OnboardingCompleted',
}

export type UserResponse = {
  id: string;
  email: string;
  role: EUserRole;
  descriptions: string;
  name: string | null;
  country: Country | null;
  lastLogin: string | null;
  dateOfRegistration: string;
  vaccinesCount: number;
  peoplesCount: number;
  dogsCount: number;
  catsCount: number;
  expoTokensCount: number;
  language: string | null;
  platform: UserPlatform | null;
  stage: EUserOnboardingStage;
};

// Старый тип для совместимости (если используется в других местах)
export type UserResponseOld = { _id: string } & Omit<User, 'token'>;

export type Pagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: Pagination;
};

export type GetUsersParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  role?: EUserRole;
  country?: string;
  language?: string;
  stage?: EUserOnboardingStage;
};

export enum EUserDeletionSource {
  SelfDelete = 'self_delete',
  AdminDelete = 'admin_delete',
}

export type DeletedUserItem = {
  userId: string; // бывший id удалённого аккаунта
  email: string | null; // null, если не сохранился
  source: EUserDeletionSource;
  deletedAt: string; // ISO 8601
};

export type GetDeletedUsersParams = {
  filter?: string; // литеральный substring-поиск по email (не regex)
  source?: EUserDeletionSource;
  from?: string; // YYYY-MM-DD; UTC-календарные дни
  to?: string; // YYYY-MM-DD; бек растягивает до конца дня
  sortBy?: 'deletedAt' | 'email';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number; // дефолт бека — 20, допустимо 1..100
};

export enum ETypePets {
  Cat = 'cat',
  Dog = 'dog',
  All = 'all',
}

export type TDiseaseRound = {
  type_round: ENextRoundVariant;
  type_vaccination: ETypeVaccine;
  count_days?: number;
  count_days2?: number;
  count_years?: number;
  isPeriodicRevactination: boolean;
};

export enum EDiseaseNoteStatus {
  Info = 'info',
  Alert = 'alert',
}

export type TDiseaseCountry = {
  countryCode: string;
  countryName: string;
  status: EDiseaseStatus;
  completed: boolean;
  note?: string;
  note_status?: EDiseaseNoteStatus;
  age_shedule: Array<{
    start: number;
    end: number;
    rounds: TDiseaseRound[];
  }>;
};

export type TDiseaseAlert = {
  countryCode: string;
  note: string;
};

export type VaccineRef = {
  _id: string;
  name: string;
  name_ru: string;
};

export type Disease = {
  _id: string;
  name: string;
  name_ru: string;
  code: string;
  type_pets?: ETypePets;
  vaccine_list: VaccineRef[];
  verified: boolean;
  isIncludePastDiseases?: boolean;
  descriptions?: string;
  descriptions_en?: string;
  notes?: string;
  countries: TDiseaseCountry[];
};

export enum ENextRoundVariant {
  AnyDays = 'any_days', // любой день
  DateOfBirth = 'from_the_birth', // от рождения человека
  FirstRound = 'first_round', //  от начала вакцинирования (от первого раунда)
  TimeOutRound = 'time_out_round',
}

export enum ENextRound {
  any_days = 'В любой день',
  from_the_birth = 'От даты рождения',
  first_round = 'От даты начала вакцинирования (от первого раунда)',
  time_out_round = 'От даты последней вакцинации',
}

export enum EVaccineType {
  alive = 'Live',
  inactivated = 'Inactivated',
  anatoxin = 'Anatoxin',
  molecular = 'Molecular',
}

export type TVaccineRound = {
  type_round: ENextRoundVariant;
  count_days?: number;
  count_days2?: number;
  type_vaccination: ETypeVaccine;
  count_years?: number;
  isPeriodicRevactination: boolean;
};

export type TVaccineAgeShedule = {
  start: number;
  end: number;
  rounds: TVaccineRound[];
};

export type VaccineDiseaseRef = {
  id: string;
  name_en: string;
  verified: boolean;
};

export type Vaccine = {
  _id: string;
  name: string;
  name_ru: string;
  code: string;
  manufacturerCountry: string;
  manufacturer: string;
  pharmaceuticalCompanyId?: string;
  availableCountries: string[];
  vaccine_type: EVaccineType;
  age_shedule: TVaccineAgeShedule[];
  verified: boolean;
  diseases?: VaccineDiseaseRef[];
  descriptions?: string;
  descriptions_en?: string;

  pharmacological_properties?: string;
  compound?: string;
  indications_for_use?: string;
  contraindications?: string;
  mode_of_application?: string;
  precautions_for_use?: string;
  carefully?: string;
  special_instructions?: string;
  side_effect?: string;
  use_during_pregnancy_and_breastfeeding?: string;
  interaction_with_other_drugs?: string;
  storage_conditions?: string;
  transportation_conditions?: string;

  notes?: string;
};

export type Breed = {
  _id: string;
  specie: ESpecie;
  code: string;
  name: string;
  name_ru: string;
  descriptions?: string;
  descriptions_ru?: string;
  verified: boolean;
};
export enum ESpecie {
  Dog = 'dog',
  Cat = 'cat',
}

export type GetBreedsParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  specie: ESpecie;
};

export type GetVaccinesParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  verified?: boolean;
  petType?: ETypePets;
};

export type VaccineListItem = {
  id: string;
  name: string;
  manufacturer: string;
  manufacturerCountry: string;
  availableCountriesCount: number;
  verified: boolean;
  usedInDiseases: Array<{
    id: string;
    name_en: string;
    verified: boolean;
  }>;
};

// Disease params and list item
export type GetDiseasesParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  verified?: boolean;
  type_pets?: 'dog' | 'cat';
};

export type DiseaseListItem = {
  id: string;
  name: string;
  code: string;
  type_pets?: 'dog' | 'cat';
  verified: boolean;
  descriptions?: string;
  mandatory: number;
  basic: number;
  additional: number;
  epidemic: number;
  notDistributed: number;
  notDistributedCountryCodes: Country[];
  vaccines: Array<{
    id: string;
    name: string;
    verified: boolean;
  }>;
  alerts: TDiseaseAlert[];
};

// Vaccine Pets list item
export type VaccinePetsListItem = {
  id: string;
  name: string;
  manufacturer: string;
  manufacturerCountry: string;
  availableCountriesCount: number;
  verified: boolean;
  petType: 'cat' | 'dog' | 'all';
  usedInDiseases: Array<{
    id: string;
    name: string;
    verified: boolean;
  }>;
};

// Country params and list items
// Паспорт страны: нацкалендарь, источники, дата последней проверки
export type CountryScope = 'people' | 'pets';

export type CountryLink = {
  url: string;
  title: string;
};

export type CountryAttachment = {
  id: string;
  name: string;
  size: number;
  /** Имя файла в хранилище, скачивается через GET /files/doc/{path} */
  path: string;
};

export type CountryInfo = {
  description: string | null;
  links: CountryLink[];
  attachments: CountryAttachment[];
  /** ISO-строка даты последней проверки */
  lastCheckedAt: string | null;
  /** Считается на беке: даты нет или она старше 180 дней */
  needsCheck: boolean;
};

export type UpdateCountrySectionDto = {
  /** Пустая строка очищает поле на беке */
  description?: string;
  /** Полная замена массива ссылок, а не патч отдельных элементов */
  links?: CountryLink[];
  /** true проставляет lastCheckedAt текущим моментом */
  updateLastChecked?: boolean;
};

export type GetCountriesParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  /** true — только требующие проверки, false — только актуальные */
  needsCheck?: boolean;
};

export type DiseaseRefWithCompleted = {
  id: string;
  name_en: string;
  verified: boolean;
  completed: boolean;
};

export type CountryPeopleListItem = {
  countryCode: string;
  countryName: string;
  Mandatory: DiseaseRefWithCompleted[];
  Basic: DiseaseRefWithCompleted[];
  Additional: DiseaseRefWithCompleted[];
  Epidemic: DiseaseRefWithCompleted[];
  NotDistributed: DiseaseRefWithCompleted[];
  /** Отсутствие info — пустой паспорт, а не ошибка */
  info?: CountryInfo;
};

export type CountryPetsListItem = {
  countryCode: string;
  countryName: string;
  Mandatory: DiseaseRefWithCompleted[];
  Basic: DiseaseRefWithCompleted[];
  Additional: DiseaseRefWithCompleted[];
  Epidemic: DiseaseRefWithCompleted[];
  NotDistributed: DiseaseRefWithCompleted[];
  /** Отсутствие info — пустой паспорт, а не ошибка */
  info?: CountryInfo;
};

// Disease params and list item
export const VOICE_SORT_FIELDS = ['name', 'countryCode', 'createdAt', 'updatedAt'] as const;

export type VoiceSortField = typeof VOICE_SORT_FIELDS[number];

export type GetVoicesParams = {
  filter?: string;
  sortBy?: VoiceSortField;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  countryCode?: string;
  language?: string;
  target?: VoiceTarget;
  status?: VoiceStatus;
  isPublished?: boolean; // "false" из query бэк сам превращает в boolean
};

export enum VoiceTarget {
  Pets = 'pets',
  People = 'people',
}

/** Статус работы с автором. Только админка, мобилке не отдаётся. */
export enum VoiceStatus {
  NotContacted = 'not_contacted',
  InDiscussion = 'in_discussion',
  Posted = 'posted',
  Declined = 'declined',
}

export const VOICE_NOTES_MAX_LENGTH = 5000;

export enum SocialLinkType {
  Website = 'website',
  Facebook = 'facebook',
  Youtube = 'youtube',
  Twitter = 'twitter',
  Instagram = 'instagram',
  Tiktok = 'tiktok',
  Telegram = 'telegram',
  Threads = 'threads',
}

export type SocialLinks = {
  type: SocialLinkType;
  label?: string;
  url: string;
};

export type AvatarFile = {
  _id: string;
  size: string;
  name: string;
  path: string;
};

/** Текст, привязанный к языку. Формат bio/specialty автора начиная с API 2.0.2. */
export type LocalizedText = {
  language: string;
  text: string;
};

export type Voices = {
  _id: string;
  name: string;
  countryCode: string; // maxLength: 2 Код страны (ISO 3166-1 alpha-2)
  languages: string[]; // Языки автора (ISO 639-1 коды)
  avatar: string | AvatarFile;
  bio: LocalizedText[]; // обязателен, обязана быть запись с language === 'en'
  specialty: LocalizedText[];
  socialLinks: SocialLinks[];
  target?: VoiceTarget; // отсутствие = подходит и pets, и people
  notes?: string; // внутренние заметки, только админка
  status?: VoiceStatus; // у старых документов поля нет до бэкфилла = not_contacted
  isPublished: boolean; // админский ответ всегда нормализует: нет поля = true
  createdAt?: string;
  updatedAt?: string;
};

// Support Messages
export enum ESupportMessageStatus {
  New = 'new',
  Read = 'read',
  Replied = 'replied',
}

export enum ESupportMessageSubject {
  AccountIssue = 'accountIssue',
  TechnicalIssue = 'technicalIssue',
  VaccinationScheduleIssue = 'vaccinationScheduleIssue',
  Suggestions = 'suggestions',
  DataConsent = 'dataConsent',
  Other = 'other',
}

export type SupportMessagePhoto = {
  _id: string;
  size: number;
  name: string;
  path: string;
};

export type SupportMessage = {
  _id: string;
  subject: ESupportMessageSubject;
  email: string;
  text: string;
  status: ESupportMessageStatus;
  photos: SupportMessagePhoto[];
  adminReplyText?: string | null;
  repliedAt?: string | null;
  createdAt: string;
};

export type SupportMessageListItem = {
  _id: string;
  subject: ESupportMessageSubject;
  text: string;
  email: string;
  status: ESupportMessageStatus;
  adminReplyText?: string | null;
  repliedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  language: string | null;
  attachmentsCount: number;
};

// Pharmaceutical Companies
export type PharmaceuticalCompany = {
  _id: string;
  fullName: string;
  abbreviatedName: string;
  russianName?: string;
  country: string;
  www?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PharmaceuticalCompanyListItem = {
  id: string;
  fullName: string;
  abbreviatedName: string;
  russianName?: string;
  country: string;
  www?: string;
  notes?: string;
  vaccineCount: number;
  vaccineCountHuman?: number;
  vaccineCountPets?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type PharmaceuticalCompanyLightItem = {
  id: string;
  abbreviatedName: string;
  country: string;
};

export type PharmaceuticalCompanyVaccineRef = {
  id: string;
  name: string;
  code: string;
  verified: boolean;
};

export type PharmaceuticalCompanyDetail = {
  id: string;
  fullName: string;
  abbreviatedName: string;
  russianName?: string;
  country: string;
  www?: string;
  notes?: string;
  vaccinesHuman: PharmaceuticalCompanyVaccineRef[];
  vaccinesPets: PharmaceuticalCompanyVaccineRef[];
  createdAt?: string;
  updatedAt?: string;
};

export type CreatePharmaceuticalCompanyDto = {
  fullName: string;
  abbreviatedName: string;
  russianName?: string;
  country: string;
  www?: string;
  notes?: string;
};

export type UpdatePharmaceuticalCompanyDto = Partial<CreatePharmaceuticalCompanyDto>;

export type GetPharmaceuticalCompaniesParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  byCountry?: string;
};

export type UpdatePharmaceuticalCompanyResponse = {
  company: PharmaceuticalCompany;
  cascadedVaccines: number;
};

export type GetSupportMessagesParams = {
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  status?: ESupportMessageStatus;
  subject?: string;
  language?: string;
};

// Check code uniqueness
export type CheckCodeEntityType = 'disease' | 'diseasePets' | 'vaccine' | 'vaccinePets' | 'breed';

export type CheckCodeParams = {
  entityType: CheckCodeEntityType;
  code: string;
  excludeId?: string;
};

export type CheckCodeResponse = {
  exists: boolean;
};

// Analytics
export type AnalyticsGranularity = 'day' | 'week' | 'month' | 'half-year';

export type AnalyticsVaccinationsType = 'people' | 'pets' | 'all';

export type AnalyticsRangeParams = {
  from?: string;
  to?: string;
  granularity?: AnalyticsGranularity;
};

export type AnalyticsVaccinationsParams = AnalyticsRangeParams & {
  type?: AnalyticsVaccinationsType;
};

export type AnalyticsPoint = {
  bucket: string;
  count: number;
};

export type AnalyticsSeriesResponse = {
  series: AnalyticsPoint[];
  total: number;
};

export type UsersCountPoint = {
  bucket: string;
  registrations: number;
  // Tracked since the analytics module release; historical buckets always return 0.
  deletions: number;
};

export type UsersCountSeriesResponse = {
  series: UsersCountPoint[];
  totalRegistrations: number;
  totalDeletions: number;
};

// ===== Analytics — new endpoints =====

export type ActiveUsersResponse = {
  dau: number;
  wau: number;
  mau: number;
  dauMauRatio: number | null; // null when mau === 0
  totalUsers: number;
  asOf: string; // ISO, UTC
};

export type ActiveUsersTrendPoint = {
  bucket: string; // ISO, UTC bucket start
  dau: number;
  wau: number;
  mau: number;
};

export type ActiveUsersTrendResponse = {
  series: ActiveUsersTrendPoint[];
};

export type GeographyMetric = 'users' | 'people' | 'pets';

export type GeographyRow = {
  code: string | null; // ISO alpha-2; null = "Не указано"
  name: string;
  users: number;
  people: number;
  pets: number;
  dogs: number;
  cats: number;
};

export type GeographyTotals = {
  users: number;
  people: number;
  pets: number;
  dogs: number;
  cats: number;
};

export type GeographyResponse = {
  series: GeographyRow[];
  totals: GeographyTotals;
};

export type ProfileFillRateMetric = {
  total: number;
  perAccount: number;
};

export type ProfileFillRateResponse = {
  accounts: number;
  accountsWithData: number;
  people: ProfileFillRateMetric;
  pets: ProfileFillRateMetric;
  vaccinations: ProfileFillRateMetric;
  documents: ProfileFillRateMetric;
};

export type LanguagesPoint = {
  language: string; // ISO 639-1 or "unknown"
  count: number;
};

export type LanguagesResponse = {
  series: LanguagesPoint[];
  total: number;
};

// ===== Registration funnel (/analytics/registration-funnel) =====

export type FunnelStage = {
  stage: EUserOnboardingStage;
  count: number;
  percent: number; // число с точностью до одного знака (25, не '25.0') — форматировать на фронте
};

// Бек типизирует sources как string[]; сейчас пишет только эти три значения —
// на фронте обрабатывать неизвестные строки с fallback-лейблом
export type VaccinationSource = 'ownerPeople' | 'otherPeople' | 'pets';

export type SourceCombination = {
  sources: string[];
  count: number;
};

export type CompletedBySource = {
  ownerPeople: number; // три счётчика пересекаются между собой
  otherPeople: number;
  pets: number;
  combinations: SourceCombination[]; // непересекающееся разбиение; порядок — по убыванию count, не канонический
};

export type RegistrationFunnelResponse = {
  calculatedAt: string | null; // null — cron ещё ни разу не отрабатывал; total=0 при непустом значении — валидный снимок
  total: number;
  funnel: FunnelStage[]; // всегда 4 стадии в порядке воронки, включая нулевые
  completedBySource: CompletedBySource;
};

// ===== Translations =====

export enum EEntityType {
  Disease = 'disease',
  DiseasePets = 'diseasePets',
  Vaccine = 'vaccine',
  VaccinePets = 'vaccinePets',
  Breed = 'breed',
}

export enum ETranslationStatus {
  All = 'all',
  Missing = 'missing',
  Translated = 'translated',
}

export type LanguageListItem = {
  code: string;
  name: string;
  nativeName: string;
};

export type CoverageCell = {
  translated: number;
  total: number;
  percent: number;
};

export type CoverageLanguage = {
  language: string;
  disease: CoverageCell;
  diseasePets: CoverageCell;
  vaccine: CoverageCell;
  vaccinePets: CoverageCell;
  breed: CoverageCell;
  overall: CoverageCell;
};

export type TranslationFields = {
  name: string;
  descriptions: string;
};

export type TranslationEntityItem = {
  entityId: string;
  code: string;
  source: TranslationFields;
  translation: TranslationFields | null;
  translated: boolean;
  missingFields: string[];
  updatedAt: string | null;
};

export type GetTranslationEntitiesParams = {
  language: string;
  status?: ETranslationStatus;
  filter?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export type UpsertTranslationDto = {
  entityType: EEntityType;
  entityId: string;
  language: string;
  fields: TranslationFields;
};

export type TranslationRecord = {
  _id: string;
  entityType: EEntityType;
  entityId: string;
  language: string;
  code: string;
  fields: Record<string, string>;
  updatedAt: string;
};
